import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './AIMenu.module.scss';
import { useAIMenu } from './useAIMenu';
import { GhostSelectionOverlay } from './GhostSelectionOverlay';
import { COMPANY_CHIPS, SELECTION_CHIPS, CHIP_PROMPT_MAP } from './aiMenuConstants';

export interface AIMenuHandle {
    toggle: () => void;
}

export interface AIMenuProps {
    /** Subject company org id, forwarded to the askAica RAG agent. */
    aiOrgId?: string;
    /** Extra company chips appended to COMPANY_CHIPS in the no-selection row. */
    extraCompanyChips?: string[];
}

function getCaretPosition(): { left: number; top: number } | null {
    const sel = document.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    // For a non-collapsed selection, anchor at the focus end (where the cursor
    // actually is). The whole-selection bounding rect can be far below the
    // viewport for a tall/multi-screen selection, which would push the fixed
    // panel off-screen.
    if (!sel.isCollapsed && sel.focusNode) {
        try {
            const focusRange = document.createRange();
            focusRange.setStart(sel.focusNode, sel.focusOffset);
            focusRange.collapse(true);
            const fRect = focusRange.getBoundingClientRect();
            if (fRect.height > 0) return { left: fRect.left, top: fRect.bottom + 4 };
        } catch {
            // fall through to the bounding-rect logic below
        }
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) {
        const collapsed = range.cloneRange();
        collapsed.collapse(true);
        const caretRect = collapsed.getBoundingClientRect();
        if (caretRect.height === 0) return null;
        return { left: caretRect.left, top: caretRect.bottom + 4 };
    }
    return { left: rect.left + rect.width / 2, top: rect.bottom + 4 };
}

// Keep a panel position reachable on-screen. `left` is the panel's CENTRE-x
// (the .menu transform is translateX(-50%)), so clamp against half the width.
function clampToViewport(left: number, top: number, halfWidth: number): { left: number; top: number } {
    const margin = 8;
    return {
        left: Math.min(Math.max(left, halfWidth + margin), window.innerWidth - halfWidth - margin),
        top: Math.min(Math.max(top, margin), window.innerHeight - 60),
    };
}

export const AIMenu = forwardRef<AIMenuHandle, AIMenuProps>(function AIMenu({ aiOrgId, extraCompanyChips = [] }, ref) {
    const {
        isOpen,
        close,
        toggle,
        prompt,
        setPrompt,
        streaming,
        error,
        result,
        turns,
        hadSelection,
        savedSelection,
        submit,
        accept,
        insertBelow,
        discard,
        clear,
        retry,
        abort,
    } = useAIMenu({ orgId: aiOrgId });
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
    const [dragging, setDragging] = useState(false);
    const dragOriginRef = useRef<{ px: number; py: number; left: number; top: number } | null>(null);

    useImperativeHandle(ref, () => ({ toggle }), [toggle]);

    useEffect(() => {
        if (isOpen) {
            const base = getCaretPosition() ?? { left: window.innerWidth / 2, top: window.innerHeight / 3 };
            const halfW = menuRef.current?.offsetWidth ? menuRef.current.offsetWidth / 2 : 210;
            setPos(clampToViewport(base.left, base.top, halfW));
            requestAnimationFrame(() => inputRef.current?.focus());
        } else {
            setPos(null);
        }
    }, [isOpen]);

    // Close on click outside. We deliberately do NOT render a full-screen
    // backdrop — one would sit above the editor's inner scroll container and
    // swallow wheel events, making the page unscrollable while the menu (and
    // its preview) is open. A document-level pointerdown listener gives the
    // same dismiss behavior without blocking scroll. Skip clicks on the AI
    // trigger button so re-clicking it toggles cleanly instead of racing the
    // close into a re-open. Attach on the next frame so the click that opened
    // the menu doesn't immediately close it.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onPointerDown = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (menuRef.current?.contains(target)) return;
            if (target.closest('[data-ai-trigger]')) return;
            close();
        };
        const raf = requestAnimationFrame(() => document.addEventListener('pointerdown', onPointerDown, true));
        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('pointerdown', onPointerDown, true);
        };
    }, [isOpen, close]);

    // Drag-to-move. The grip starts a drag; window listeners track the pointer
    // so it keeps moving even if the cursor outruns the panel. pos.left is the
    // panel's CENTRE-x (the .menu transform is translateX(-50%)), so we clamp
    // against half the width to keep it reachable on-screen.
    useEffect(() => {
        if (!dragging) return undefined;
        const onMove = (e: PointerEvent) => {
            const o = dragOriginRef.current;
            if (!o) return;
            const el = menuRef.current;
            const halfW = el ? el.offsetWidth / 2 : 210;
            setPos(clampToViewport(o.left + (e.clientX - o.px), o.top + (e.clientY - o.py), halfW));
        };
        const onUp = () => {
            setDragging(false);
            dragOriginRef.current = null;
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, [dragging]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                if (streaming) abort();
                else close();
            } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (streaming) return;
                // Enter always submits the current prompt. Committing a result is
                // an explicit button (Replace / Insert) so it can't fire by accident.
                submit();
            }
        },
        [streaming, abort, close, submit],
    );

    const startDrag = useCallback(
        (e: ReactPointerEvent) => {
            if (!pos) return;
            e.preventDefault();
            dragOriginRef.current = { px: e.clientX, py: e.clientY, left: pos.left, top: pos.top };
            setDragging(true);
        },
        [pos],
    );

    // Clicking a hint chip fills the prompt and refocuses the textarea so the
    // user can tweak it and press Enter — a quick way to start a common request.
    const applyChip = useCallback(
        (chipLabel: string) => {
            // Some chips (e.g. the pitch draft) expand to a fuller prompt; the
            // rest submit their label verbatim.
            const promptText = CHIP_PROMPT_MAP[chipLabel] ?? chipLabel;
            setPrompt(promptText);
            requestAnimationFrame(() => {
                const el = inputRef.current;
                if (!el) return;
                el.focus();
                // Caret to end so the filled text is editable, not selected.
                el.setSelectionRange(promptText.length, promptText.length);
            });
        },
        [setPrompt],
    );

    if (!isOpen || !pos) return null;

    // Conversation = any completed turn or an in-flight stream. In that state the
    // input shrinks (.withResult) and the transcript takes the dialog height.
    const hasConversation = turns.length > 0 || streaming;
    const showActions = !streaming && turns.length > 0;
    const showChips = !hasConversation && !prompt.trim();

    return (
        <>
            <GhostSelectionOverlay savedSelection={savedSelection} />
            {createPortal(
                <div
                    ref={menuRef}
                    className={[styles.menu, dragging && styles.menuDragging, hasConversation && styles.withResult]
                        .filter(Boolean)
                        .join(' ')}
                    style={{ left: pos.left, top: pos.top }}
                >
                    <div className={styles.dragHandle} onPointerDown={startDrag} role="presentation" aria-hidden title="Drag to move">
                        <span className={styles.dragGrip} />
                    </div>
                    <div className={styles.inputRow}>
                        <span className={styles.sparkle} aria-hidden>
                            ✦
                        </span>
                        <textarea
                            ref={inputRef}
                            className={styles.input}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={hadSelection ? 'Tell AICA how to edit the selection…' : 'Ask AICA about this company…'}
                            disabled={streaming}
                            aria-label="AI prompt"
                        />
                        <button
                            type="button"
                            className={styles.submitBtn}
                            onClick={submit}
                            disabled={!prompt.trim() || streaming}
                            aria-label="Submit"
                            title="Submit (Enter · Shift+Enter for newline)"
                        >
                            ↵
                        </button>
                    </div>

                    {showChips && (
                        <div className={styles.chips}>
                            {(hadSelection ? SELECTION_CHIPS : [...new Set([...COMPANY_CHIPS, ...extraCompanyChips])]).map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    className={styles.chip}
                                    onClick={() => applyChip(c)}
                                    disabled={streaming}
                                    title={`Use: ${c}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    )}

                    {hasConversation && (
                        <div className={styles.transcript} aria-live="polite">
                            {turns.length > 1 && (
                                <div className={styles.transcriptHead}>
                                    <button type="button" className={styles.clearBtn} onClick={clear} title="Clear conversation">
                                        Clear
                                    </button>
                                </div>
                            )}
                            {turns.map((t) => (
                                <div key={t.id} className={styles.turn}>
                                    <div className={styles.userMsg}>{t.prompt}</div>
                                    <div className={styles.assistantMsg}>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.response}</ReactMarkdown>
                                    </div>
                                </div>
                            ))}
                            {streaming && (
                                <div className={styles.turn}>
                                    {!!prompt.trim() && <div className={styles.userMsg}>{prompt}</div>}
                                    <div className={styles.assistantMsg}>
                                        {result ? (
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{result}</ReactMarkdown>
                                        ) : (
                                            <span className={styles.previewPlaceholder}>…</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {streaming && (
                        <div className={styles.statusRow}>
                            <span className={styles.spinner} aria-hidden />
                            <span>Generating…</span>
                            <button type="button" className={styles.stopBtn} onClick={abort}>
                                Stop
                            </button>
                        </div>
                    )}

                    {showActions && (
                        <div className={styles.actions}>
                            <button type="button" className={styles.acceptBtn} onClick={accept}>
                                {hadSelection ? 'Replace selection' : 'Insert'}
                            </button>
                            {hadSelection && (
                                <button type="button" className={styles.actionBtn} onClick={insertBelow}>
                                    Insert below
                                </button>
                            )}
                            <button type="button" className={styles.actionBtn} onClick={retry}>
                                Try again
                            </button>
                            <button type="button" className={styles.discardBtn} onClick={discard}>
                                Discard
                            </button>
                        </div>
                    )}

                    {error && (
                        <div className={styles.errorRow}>
                            <span>⚠</span>
                            <span>{error}</span>
                        </div>
                    )}

                    {!hasConversation && !error && (
                        <div className={styles.hint}>⌘J to toggle · Enter to submit · Esc to close · stays open if dismissed</div>
                    )}
                </div>,
                document.body,
            )}
        </>
    );
});
